<script lang="ts" setup>
import type { AsyncDataRequestStatus, NuxtError } from '#app'
import type { Head } from 'zhead'

import BrandReviews from '~/components/reviews/BrandReviews.vue'
import { useSharing } from '~/composables/sharing'
import { useWindowHelpers } from '~/composables/window-helpers'
import ImageType from '~/enums/image-type'
import PageEnum from '~/enums/page'
import ProductSortEnum from '~/enums/product-sort'
import helpers from '~/helpers'
import JsonApiHelper from '~/helpers/json-api-helper'
import type { BrandData } from '~/types/brands'
import type { BreadcrumbsItem } from '~/types/breadcrumbs'
import type { Page } from '~/types/json-api'
import type { ProductData } from '~/types/products'

const breadcrumbsItems: BreadcrumbsItem[] = [
  { name: 'Главная', path: PageEnum.Main },
  { name: 'Бренды', path: PageEnum.Brands }
]
const imageTypes = [ImageType.Thumbnail144p, ImageType.Original]
const previewTypes = [ImageType.Thumbnail144p, ImageType.Original]

const { parsePromoCodeFromQuery } = useSharing()
const { scrollTo, scrollToSavedPosition } = useWindowHelpers()
const { $apiHelper } = useNuxtApp()
const route = useRoute()
const router = useRouter()

const actualFullPath = ref<string | null>(null)
const isFirstDataLoading = ref(true)
const isProductsDataReloading = ref(false)

// Sort.
const sort = ref(ProductSortEnum.MostPopular)

// Pagination.
const page = ref<Page>({ number: 1, size: 30 })

const bannerImagePaths = computed(() => {
  switch (brandId.value) {
    case '204':
      return ['/images/banners/65_b.jpg', '/images/banners/65_b_small.jpg']
    case '321':
      return ['/images/banners/90_b.jpg', '/images/banners/90_b_small.jpg']
    case '382':
      return ['/images/banners/91_b.jpg', '/images/banners/91_b_small.jpg']
    case '40':
      return ['/images/banners/92_b.jpg', '/images/banners/92_b_small.jpg']
    default:
      return []
  }
})
const brandId = computed(() => helpers.getIdFromSlugAndId(route.params.slugAndId as string))
const brandKey = computed(() => `brand-${brandId.value}`)
const descriptionHtml = computed(() => brandData.value!.attributes.descriptionHtml)
const imageUrl = computed(() => {
  const imageUrls = helpers.getImageUrls(brandData.value!.relationships.images.data)

  return imageUrls ? helpers.getImageUrl(imageUrls, imageTypes) : null
})
const name = computed(() => brandData.value!.attributes.name)
const seoBottomDescriptionHtml = computed(() => seoData.value?.bottomDescriptionHtml ?? null)
const seoData = computed(() => brandData.value!.attributes.seoData)
const seoH1 = computed(() => seoData.value?.h1 ?? name.value)


const { data, error, status } = await useLazyAsyncData(
  brandKey,
  async () => {
    parseQuery()

    try {
      const [brandResponse, productsResponse] = await Promise.all([
        $apiHelper.brands.getBrand(brandId.value!),
        $apiHelper.products.getProducts(getProductsParams())
      ])

      const { fullPath, path } = route
      const actualPath = helpers.getBrandPath(brandResponse.data)

      if (path !== actualPath) {
        actualFullPath.value = fullPath.replace(path, actualPath)

        if (import.meta.client) {
          history.replaceState({}, '', actualFullPath.value)
        }
      }

      return { brandResponse, productsResponse }
    } catch (error) {
      showError(error as NuxtError)
    }
  },
  {
    transform: (x) => ({
      brandResponse: JsonApiHelper.denormalizeResponse(x?.brandResponse),
      productsResponse: JsonApiHelper.denormalizeResponse(x?.productsResponse)
    })
  }
)

if (error.value) {
  throw createError(error.value as NuxtError)
}

if (actualFullPath.value) {
  await navigateTo(actualFullPath.value, { redirectCode: 301 })
}

const brandData = computed<BrandData | null>(() => data.value?.brandResponse?.data ?? null)
const productsData = computed<ProductData[]>(() => data.value?.productsResponse?.data ?? [])
const totalProducts = computed(() => data.value?.productsResponse?.meta.totalProducts)
const totalPages = computed(
  () => data.value?.productsResponse
    ? helpers.getTotalPages(data.value?.productsResponse.meta.totalProducts, page.value.size)
    : null
)

onBeforeMount(parseQuery)

onMounted(async (): Promise<void> => {
  isFirstDataLoading.value = false

  await parsePromoCodeFromQuery()
})

useHead(() => {
  if (!brandData.value) {
    return {}
  }

  const head: Head = {
    title: seoData.value?.title ?? `${seoH1.value} — каталог товаров бренда, цены, купить продукцию ${seoH1.value} в интернет-магазине iTAB`,
    meta: [{
      name: 'description',
      content: seoData.value?.description ?? `Широкий ассортимент товаров ${seoH1.value} в каталоге интернет-магазина iTAB. Ознакомьтесь с информацией о бренде, каталогом его продукции и оформите заказ с доставкой по Москве и всей России.`
    }]
  }

  const previewUrl = getPreviewUrl()

  if (head.meta && previewUrl) {
    head.meta.push(...helpers.getPreviewMetadata(previewUrl))
  }

  return head
})

watch(
  [page, sort],
  async (): Promise<void> => {
    if (!isFirstDataLoading.value) {
      scrollTo('scroll-anchor', 60)

      await updateQuery()

      await reloadProductsData()
    }
  },
  { deep: true }
)
watch(
  sort,
  (): void => {
    if (isFirstDataLoading.value) {
      return
    }

    changePage(1)
  }
)
watch(
  status,
  (x: AsyncDataRequestStatus): void => {
    if (x === 'success') {
      nextTick(scrollToSavedPosition)
    }
  }
)

function changePage (pageNumber: number): void {
  page.value.number = pageNumber
}

function getPreviewUrl (): string | null {
  const imageUrls = helpers.getImageUrls(brandData.value!.relationships.images.data)

  return imageUrls ? helpers.getImageUrl(imageUrls, previewTypes) : null
}

function getProductsParams (): any {
  const { number, size } = page.value

  return helpers.getParams([
    { name: 'filter[brandId]', value: brandId.value, condition: true },
    { name: 'page[number]', value: number, condition: true },
    { name: 'page[size]', value: size, condition: true },
    { name: 'sort', value: sort.value, condition: sort.value }
  ])
}

function parseQuery (): void {
  const { page: _page, sort: _sort } = route.query
  const { MostPopular } = ProductSortEnum

  page.value.number = _page !== undefined && /^\+?[1-9]\d*$/.test(String(_page)) && _page !== '1' ? Number(_page) : 1
  sort.value = _sort !== undefined && Object.values(ProductSortEnum).includes(String(_sort)) && _sort !== MostPopular
    ? _sort
    : MostPopular
}

async function reloadProductsData (): Promise<void> {
  isProductsDataReloading.value = true

  const response = await $apiHelper.products.getProducts(getProductsParams())
  const { data: productsData } = JsonApiHelper.denormalizeResponse(response)

  if (data.value?.productsResponse) {
    data.value.productsResponse.data = productsData
  }

  isProductsDataReloading.value = false
}

async function updateQuery (): Promise<void> {
  const { number } = page.value

  const newQuery: any = {}

  if (number !== null && number !== 1) {
    newQuery.page = number
  }

  if (sort.value !== null && sort.value !== ProductSortEnum.MostPopular) {
    newQuery.sort = sort.value
  }

  // To avoid an error: "Avoided redundant navigation to current location".
  try {
    await router.replace({ query: newQuery })
  } catch {}
}
</script>

<template>
  <div>
    <section v-if="status === 'success'">
      <div class="container">
        <Breadcrumbs :current="name" :items="breadcrumbsItems" />
      </div>
    </section>

    <Preloader v-if="status === 'pending'" />
    <template v-else-if="status === 'success' && brandData">
      <section>
        <div class="container">
          <Heading :badge-text="totalProducts" :title="seoH1" />

          <ThinBanner :banner-image-paths="bannerImagePaths" class="mb-6" :title="name" />

          <div class="overflow-hidden">
            <div
              v-if="imageUrl"
              class="
                float-left mb-6 mr-6 size-[72px] rounded-2xl bg-white p-2
                sm:size-[144px] sm:rounded-xl sm:p-4
              "
            >
              <div class="relative overflow-hidden pb-[100%]">
                <img
                  v-lazy="imageUrl"
                  :alt="name"
                  class="absolute size-full min-h-full object-contain"
                  loading="lazy"
                >
              </div>
            </div>

            <HtmlArea v-if="descriptionHtml" class="overflow-initial mb-6" :plain-html="descriptionHtml" />
          </div>
        </div>
      </section>

      <Preloader v-if="isProductsDataReloading" />
      <ProductsWithPagination
        v-else
        v-model:sort="sort"
        :no-products-text="`Нет товаров бренда «${name}».`"
        :page="page"
        :products-data="productsData"
        :total-pages="totalPages"
        @change-page="changePage"
      />
    </template>

    <section class="mt-10">
      <div class="container">
        <BrandReviews v-if="brandId" :brand-id="brandId" class="mt-10" />

        <HtmlArea
          v-if="status === 'success' && seoBottomDescriptionHtml"
          class="mt-6 text-sm"
          :plain-html="seoBottomDescriptionHtml"
        />
      </div>
    </section>
  </div>
</template>
